import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";
import { 
  Globe, 
  Palette, 
  ExternalLink,
  CheckCircle2,
  Zap,
  Code2,
  Rocket,
  MessageSquare
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const createdWebsites = [
  // {
  //   name: "Lead Finder Tool",
  //   url: "https://findleads.lovable.app",
  //   image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=450&fit=crop",
  //   description: "Free tool to find WordPress websites in any niche. Build targeted prospect lists in seconds with powerful search filters and export capabilities.",
  //   category: "Sales Tool",
  //   features: [
  //     "Search by industry or keyword",
  //     "Export leads to CSV",
  //     "Continuous WordPress detection",
  //     "Contact information extraction"
  //   ],
  //   tech: ["React", "TypeScript", "Tailwind CSS", "API Integration"]
  // },
  {
    name: "Lakeside Support Services",
    url: "https://lakesidesupportservice.com.au",
    image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=800&h=450&fit=crop",
    description: "Professional support services website for the Lakeside community. Clean, accessible design with clear service information and easy contact options.",
    category: "Business Website",
    features: [
      "Responsive mobile-first design",
      "Service showcase sections",
      "Contact form integration",
      "SEO optimized content"
    ],
    tech: ["React", "TypeScript", "Tailwind CSS", "Supabase"]
  },
];

const capabilities = [
  {
    icon: Zap,
    title: "Rapid Development",
    description: "From concept to launch in days, not months. We use modern tools to deliver fast without sacrificing quality."
  },
  {
    icon: Code2,
    title: "Modern Tech Stack",
    description: "Built with React, TypeScript, and Tailwind CSS for maintainable, scalable applications."
  },
  {
    icon: Palette,
    title: "Custom Design",
    description: "Unique designs tailored to your brand. No cookie-cutter templates here."
  },
  {
    icon: Rocket,
    title: "Performance Focused",
    description: "Optimized for speed and SEO. Your site will load fast and rank well."
  }
];

const WebsiteCreations = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Website Creations | ShadowScan Web Development</title>
        <meta
          name="description"
          content="Explore our web development portfolio. Custom websites and tools built with modern technology. From business sites to SaaS applications."
        />
      </Helmet>
      <Navigation />
      
      <main className="flex-grow pt-24">
        {/* Hero Section */}
        <section className="relative py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-accent/10 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl -z-10" />
          <div className="container mx-auto max-w-6xl relative z-10">
            <div className="text-center mb-12">
              <Badge variant="outline" className="mb-4 px-4 py-1 text-primary border-primary/30">
                WEB DEVELOPMENT PORTFOLIO
              </Badge>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">
                <span className="text-foreground">Website</span>{" "}
                <span className="text-gradient">Creations</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Custom websites and web applications built with modern technology. 
                From business sites to powerful SaaS tools.
              </p>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section className="py-16 px-4 bg-muted/30">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {capabilities.map((cap, index) => {
                const Icon = cap.icon;
                return (
                  <Card key={index} className="cyber-card border-border hover:border-primary/50 transition-all duration-300">
                    <CardContent className="pt-6">
                      <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center mb-4">
                        <Icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="font-semibold mb-2">{cap.title}</h3>
                      <p className="text-sm text-muted-foreground">{cap.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <div className="font-mono text-sm text-primary mb-4">// PORTFOLIO</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Featured Projects</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Explore the websites and applications we've built for clients and internal use
              </p>
            </div>

            <div className="space-y-12">
              {createdWebsites.map((site, index) => (
                <Card key={site.url} className="cyber-card border-border hover:border-primary/50 transition-all duration-300 overflow-hidden">
                  <div className={`grid lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}>
                    {/* Image */}
                    <div className={`relative aspect-video lg:aspect-auto ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                      <img 
                        src={site.image} 
                        alt={`${site.name} preview`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent lg:hidden" />
                    </div>
                    
                    {/* Content */}
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-4">
                        <Badge variant="secondary">{site.category}</Badge>
                        <Globe className="h-5 w-5 text-muted-foreground" />
                      </div>
                      
                      <CardTitle className="flex items-center gap-3 text-2xl mb-4">
                        <Palette className="h-6 w-6 text-primary" />
                        {site.name}
                      </CardTitle>
                      
                      <CardDescription className="text-base mb-6">
                        {site.description}
                      </CardDescription>

                      {/* Features */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold mb-3 text-foreground">Key Features</h4>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {site.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Tech Stack */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold mb-3 text-foreground">Built With</h4>
                        <div className="flex flex-wrap gap-2">
                          {site.tech.map((tech, i) => (
                            <Badge key={i} variant="outline" className="font-mono text-xs">
                              {tech}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <Button asChild className="gap-2 w-fit">
                        <a href={site.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                          Visit Website
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto max-w-3xl text-center">
            <div className="cyber-card rounded-2xl p-12 border-primary/30">
              <div className="font-mono text-sm text-primary mb-4">// START_PROJECT</div>
              <h2 className="text-3xl font-heading font-bold mb-4">Need a Website Built?</h2>
              <p className="text-muted-foreground mb-8">
                Let's discuss your project. We build custom websites and web applications 
                tailored to your specific needs.
              </p>
              <Button asChild size="lg" className="gap-2">
                <a href="mailto:bbridle@shadowscan.com.au">
                  <MessageSquare className="h-5 w-5" />
                  Get in Touch
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WebsiteCreations;
